<script setup lang="ts">
import { ref, watch, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import * as echarts from 'echarts/core'
import { BarChart as EBarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

echarts.use([EBarChart, GridComponent, TooltipComponent, CanvasRenderer])

interface TimeSeriesData {
	name: string
	total: number
}

interface ComparisonData {
	current: number
	previous: number
}

function getCssColor(varName: string, fallback: string): string {
	const val = getComputedStyle(document.documentElement).getPropertyValue(varName)?.trim()
	if (!val || !/^\d+/.test(val)) return fallback
	// CSS custom properties use space-separated HSL: "204 77% 47%"
	// Canvas API needs comma-separated: "hsl(204, 77%, 47%)"
	const parts = val.split(/\s+/)
	return parts.length >= 3 ? `hsl(${parts[0]}, ${parts[1]}, ${parts[2]})` : `hsl(${val})`
}

function withAlpha(hslColor: string, alpha: number): string {
	// Convert "hsl(204, 77%, 47%)" to "hsla(204, 77%, 47%, 0.5)"
	return hslColor.replace('hsl(', 'hsla(').replace(')', `, ${alpha})`)
}

const props = defineProps<{
	data: TimeSeriesData[] | ComparisonData
	size?: 'small' | 'medium' | 'large'
	height?: String
}>()

const chartRef = ref<HTMLElement | null>(null)
let chartInstance: echarts.ECharts | null = null

const renderChart = () => {
	if (!chartRef.value) return
	if (chartInstance) chartInstance.dispose()
	chartInstance = echarts.init(chartRef.value)

	const primary = getCssColor('--primary', '#3b82f6')
	const muted = getCssColor('--muted', '#f1f5f9')
	const mutedFg = getCssColor('--muted-foreground', '#64748b')
	const isTimeSeries = Array.isArray(props.data)

	const fontSizes: Record<string, number> = { small: 8, medium: 9, large: 10 }
	const fontSize = fontSizes[props.size || 'large']

	if (isTimeSeries) {
		const labels = (props.data as TimeSeriesData[]).map(d => d.name)
		const values = (props.data as TimeSeriesData[]).map(d => d.total)

		chartInstance.setOption({
			tooltip: {
				trigger: 'axis',
				backgroundColor: 'rgba(0,0,0,0.8)',
				borderWidth: 0,
				textStyle: { color: '#fff', fontSize: 11 },
				axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(0,0,0,0.04)' } }
			},
			grid: { top: 12, right: 12, bottom: 28, left: 40 },
			xAxis: {
				type: 'category',
				data: labels,
				axisLine: { show: false },
				axisTick: { show: false },
				axisLabel: { fontSize, color: mutedFg, interval: labels.length > 20 ? 2 : 0 }
			},
			yAxis: {
				type: 'value',
				splitLine: { lineStyle: { color: muted, type: 'dashed' } },
				axisLabel: { fontSize, color: mutedFg }
			},
			series: [{
				type: 'bar',
				data: values,
				barMaxWidth: 32,
				itemStyle: {
					borderRadius: [3, 3, 0, 0],
					color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
						{ offset: 0, color: primary },
						{ offset: 1, color: withAlpha(primary, 0.5) }
					])
				},
				emphasis: {
					itemStyle: {
						color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
							{ offset: 0, color: primary },
							{ offset: 1, color: withAlpha(primary, 0.8) }
						])
					}
				}
			}]
		})
	} else {
		const d = props.data as ComparisonData
		chartInstance.setOption({
			tooltip: { trigger: 'axis', backgroundColor: 'rgba(0,0,0,0.8)', borderWidth: 0, textStyle: { color: '#fff', fontSize: 11 } },
			grid: { top: 12, right: 12, bottom: 28, left: 40 },
			xAxis: {
				type: 'category',
				data: ['Current', 'Previous'],
				axisLine: { show: false },
				axisTick: { show: false },
				axisLabel: { fontSize, color: mutedFg }
			},
			yAxis: {
				type: 'value',
				splitLine: { lineStyle: { color: muted, type: 'dashed' } },
				axisLabel: { fontSize, color: mutedFg }
			},
			series: [{
				type: 'bar',
				data: [
					{ value: d.current, itemStyle: { color: primary, borderRadius: [3, 3, 0, 0] } },
					{ value: d.previous, itemStyle: { color: mutedFg, borderRadius: [3, 3, 0, 0] } }
				],
				barMaxWidth: 48
			}]
		})
	}
}

const handleResize = () => chartInstance?.resize()

watch(() => props.data, () => nextTick(renderChart), { deep: true })

onMounted(() => {
	nextTick(renderChart)
	window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
	window.removeEventListener('resize', handleResize)
	chartInstance?.dispose()
})
</script>

<template>
	<div class="chart-container" :class="size" :style="'height:' + (props.height || '300px')">
		<div ref="chartRef" class="w-full h-full"></div>
		<div v-if="!Array.isArray(data)" class="mt-2 text-sm text-center">
			<span class="font-medium">Change: </span>
			<span :class="{ 'text-emerald-500': (data.current - data.previous) >= 0, 'text-red-500': (data.current - data.previous) < 0 }">
				{{ ((data.current - data.previous) / data.previous * 100).toFixed(1) }}%
			</span>
		</div>
	</div>
</template>
